import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/api';

interface ThreatCategory {
  id: number;
  code: string;
  name: string;
  nameTranslations: Record<string, string>;
  description?: string;
  weight: number;
  threshold: number;
  earlyWarningSignals: string[];
  sortOrder: number;
  isActive: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface CategoryFormData {
  code: string;
  name: string;
  nameTranslations: {
    en: string;
    et: string;
    ru: string;
  };
  description: string;
  weight: number;
  threshold: number;
  earlyWarningSignals: string[];
  sortOrder: number;
  isActive: boolean;
}

export function CategoriesTab() {
  const { isAuthenticated } = useAuth();
  const [categories, setCategories] = useState<ThreatCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<ThreatCategory | null>(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CategoryFormData>({
    code: '',
    name: '',
    nameTranslations: {
      en: '',
      et: '',
      ru: '',
    },
    description: '',
    weight: 0.15,
    threshold: 7.0,
    earlyWarningSignals: [],
    sortOrder: 10,
    isActive: true,
  });
  const [signalInput, setSignalInput] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const categories = await apiService.getCategories(showDeleted);
      setCategories(categories);
      setError(null);
    } catch (err) {
      setError('Failed to fetch categories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [showDeleted]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCategories();
    }
  }, [isAuthenticated, fetchCategories]);

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      nameTranslations: {
        en: '',
        et: '',
        ru: '',
      },
      description: '',
      weight: 0.15,
      threshold: 7.0,
      earlyWarningSignals: [],
      sortOrder: 10,
      isActive: true,
    });
    setSignalInput('');
  };

  const handleEdit = (category: ThreatCategory) => {
    setEditingCategory(category);
    setFormData({
      code: category.code,
      name: category.name,
      nameTranslations: {
        en: category.nameTranslations['en'] || category.name,
        et: category.nameTranslations['et'] || '',
        ru: category.nameTranslations['ru'] || '',
      },
      description: category.description || '',
      weight: Number(category.weight),
      threshold: Number(category.threshold),
      earlyWarningSignals: category.earlyWarningSignals || [],
      sortOrder: category.sortOrder,
      isActive: category.isActive,
    });
    setShowAddCategory(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        nameTranslations: {
          en: formData.nameTranslations.en || formData.name,
          et: formData.nameTranslations.et,
          ru: formData.nameTranslations.ru,
        },
      };

      if (editingCategory) {
        await apiService.updateCategory(editingCategory.id, payload);
      } else {
        await apiService.createCategory(payload);
      }

      setShowAddCategory(false);
      setEditingCategory(null);
      resetForm();
      await fetchCategories();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save category');
    }
  };

  const handleDelete = async (category: ThreatCategory) => {
    const message = category.deletedAt 
      ? `Are you sure you want to permanently delete "${category.name}"? This cannot be undone.`
      : `Are you sure you want to soft delete "${category.name}"? This will preserve historical data but hide it from active use.`;
    
    if (!window.confirm(message)) return;

    try {
      if (category.deletedAt) {
        // Permanent delete
        await apiService.deleteCategory(category.id, true);
      } else {
        // Soft delete
        await apiService.deleteCategory(category.id, false);
      }
      await fetchCategories();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete category');
    }
  };

  const handleRestore = async (category: ThreatCategory) => {
    if (!window.confirm(`Are you sure you want to restore "${category.name}"?`)) return;

    try {
      await apiService.restoreCategory(category.id);
      await fetchCategories();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to restore category');
    }
  };

  const addSignal = () => {
    if (signalInput.trim()) {
      setFormData({
        ...formData,
        earlyWarningSignals: [...formData.earlyWarningSignals, signalInput.trim()]
      });
      setSignalInput('');
    }
  };

  const removeSignal = (index: number) => {
    setFormData({
      ...formData,
      earlyWarningSignals: formData.earlyWarningSignals.filter((_, i) => i !== index)
    });
  };

  // Calculate total weight
  const totalWeight = categories
    .filter(c => c.isActive && !c.deletedAt)
    .reduce((sum, c) => sum + Number(c.weight), 0);

  if (loading) {
    return <div className="tc-card p-6">Loading categories...</div>;
  }

  if (error) {
    return (
      <div className="tc-card p-6">
        <div className="text-red-600">{error}</div>
        <button onClick={fetchCategories} className="tc-btn tc-btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Categories Management */}
      <div className="tc-card p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-semibold tc-text-primary">Threat Categories</h3>
            {totalWeight !== 1 && (
              <p className="text-sm text-red-600 mt-1">
                ⚠️ Warning: Total weight is {(totalWeight * 100).toFixed(1)}% (should be 100%)
              </p>
            )}
          </div>
          <div className="space-x-2">
            <button
              onClick={() => setShowDeleted(!showDeleted)}
              className="tc-btn tc-btn-secondary"
            >
              {showDeleted ? '🔍 Hide Deleted' : '🗑️ Show Deleted'}
            </button>
            <button
              onClick={() => {
                resetForm();
                setEditingCategory(null);
                setShowAddCategory(true);
              }}
              className="tc-btn tc-btn-primary"
            >
              ➕ Add Category
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="tc-bg-secondary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Weight
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Threshold
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Translations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium tc-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="tc-bg-primary divide-y divide-gray-200">
              {categories
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((category) => (
                <tr key={category.id} className={`tc-hover ${category.deletedAt ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium tc-text-primary">
                        {category.name} ({category.code})
                      </div>
                      <div className="text-sm tc-text-secondary">
                        {category.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm tc-text-primary">
                      {(Number(category.weight) * 100).toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm tc-text-primary">
                      {Number(category.threshold).toFixed(1)}/10
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs space-y-1">
                      <div>EN: {category.nameTranslations['en'] || category.name}</div>
                      <div>ET: {category.nameTranslations['et'] || '-'}</div>
                      <div>RU: {category.nameTranslations['ru'] || '-'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {category.deletedAt ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                        🗑️ Deleted
                      </span>
                    ) : category.isActive ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        ✅ Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                        ⏸️ Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    {category.deletedAt ? (
                      <>
                        <button
                          onClick={() => handleRestore(category)}
                          className="tc-link"
                        >
                          ♻️ Restore
                        </button>
                        <button
                          onClick={() => handleDelete(category)}
                          className="tc-link text-red-600"
                        >
                          💀 Delete Forever
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEdit(category)}
                          className="tc-link"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(category)}
                          className="tc-link text-red-600"
                        >
                          🗑️ Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Category Form Modal */}
      {showAddCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="tc-card p-6 max-w-2xl max-h-[90vh] overflow-auto">
            <h3 className="text-xl font-semibold tc-text-primary mb-4">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </h3>

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium tc-text-primary mb-2">
                    Code (Uppercase, no spaces)
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/\s/g, '_') })}
                    className="tc-input w-full"
                    placeholder="MILITARY"
                    disabled={!!editingCategory}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium tc-text-primary mb-2">
                    Display Name (English)
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="tc-input w-full"
                    placeholder="Military"
                  />
                </div>
              </div>

              {/* Translations */}
              <div>
                <label className="block text-sm font-medium tc-text-primary mb-2">
                  Translations
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs tc-text-secondary">English</label>
                    <input
                      type="text"
                      value={formData.nameTranslations.en}
                      onChange={(e) => setFormData({
                        ...formData,
                        nameTranslations: { ...formData.nameTranslations, en: e.target.value }
                      })}
                      className="tc-input w-full"
                      placeholder="Military"
                    />
                  </div>
                  <div>
                    <label className="text-xs tc-text-secondary">Estonian</label>
                    <input
                      type="text"
                      value={formData.nameTranslations.et}
                      onChange={(e) => setFormData({
                        ...formData,
                        nameTranslations: { ...formData.nameTranslations, et: e.target.value }
                      })}
                      className="tc-input w-full"
                      placeholder="Sõjaline"
                    />
                  </div>
                  <div>
                    <label className="text-xs tc-text-secondary">Russian</label>
                    <input
                      type="text"
                      value={formData.nameTranslations.ru}
                      onChange={(e) => setFormData({
                        ...formData,
                        nameTranslations: { ...formData.nameTranslations, ru: e.target.value }
                      })}
                      className="tc-input w-full"
                      placeholder="Военный"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium tc-text-primary mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="tc-input w-full"
                  rows={3}
                  placeholder="Conventional and hybrid military threats..."
                />
              </div>

              {/* Weight and Threshold */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium tc-text-primary mb-2">
                    Weight (0-1)
                  </label>
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) })}
                    className="tc-input w-full"
                    min="0"
                    max="1"
                    step="0.05"
                  />
                  <p className="text-xs tc-text-tertiary mt-1">
                    Importance in overall risk calculation ({(formData.weight * 100).toFixed(0)}%)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium tc-text-primary mb-2">
                    Alert Threshold (0-10)
                  </label>
                  <input
                    type="number"
                    value={formData.threshold}
                    onChange={(e) => setFormData({ ...formData, threshold: parseFloat(e.target.value) })}
                    className="tc-input w-full"
                    min="0"
                    max="10"
                    step="0.5"
                  />
                  <p className="text-xs tc-text-tertiary mt-1">
                    Trigger alerts when score exceeds this value
                  </p>
                </div>
              </div>

              {/* Sort Order */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium tc-text-primary mb-2">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
                    className="tc-input w-full"
                    min="1"
                  />
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="mr-3"
                    />
                    <span className="text-sm font-medium tc-text-primary">Active</span>
                  </label>
                </div>
              </div>

              {/* Early Warning Signals */}
              <div>
                <label className="block text-sm font-medium tc-text-primary mb-2">
                  Early Warning Signals
                </label>
                <div className="flex space-x-2 mb-2">
                  <input
                    type="text"
                    value={signalInput}
                    onChange={(e) => setSignalInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSignal();
                      }
                    }}
                    className="tc-input flex-1"
                    placeholder="Add warning signal..."
                  />
                  <button
                    type="button"
                    onClick={addSignal}
                    className="tc-btn tc-btn-primary"
                  >
                    Add
                  </button>
                </div>
                <div className="space-y-1">
                  {formData.earlyWarningSignals.map((signal, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-100 px-3 py-1 rounded">
                      <span className="text-sm">{signal}</span>
                      <button
                        type="button"
                        onClick={() => removeSignal(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => {
                  setShowAddCategory(false);
                  setEditingCategory(null);
                  resetForm();
                }}
                className="tc-btn tc-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="tc-btn tc-btn-primary"
              >
                {editingCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}